import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const ProductViewModal = ({ isOpen, onClose, product, language, onEdit, onDelete }) => {
  if (!isOpen || !product) return null;

  const labels = {
    title: { en: 'Product Details', ar: 'تفاصيل المنتج' },
    name: { en: 'Product Name', ar: 'اسم المنتج' },
    images: { en: 'Product Images', ar: 'صور المنتج' },
    price: { en: 'Price', ar: 'السعر' },
    discountedPrice: { en: 'Discounted Price', ar: 'السعر بعد الخصم' },
    stock: { en: 'Stock Count', ar: 'الكمية المتوفرة' },
    area: { en: 'Area', ar: 'المنطقة' },
    contact: { en: 'Contact Info', ar: 'معلومات التواصل' },
    active: { en: 'Active', ar: 'نشط' },
    tags: { en: 'Tags', ar: 'العلامات' },
    description: { en: 'Description', ar: 'الوصف' },
    edit: { en: 'Edit', ar: 'تعديل' },
    delete: { en: 'Delete', ar: 'حذف' },
    close: { en: 'Close', ar: 'إغلاق' },
    noImages: { en: 'No images available', ar: 'لا توجد صور' }
  };

  const productImages = product?.media?.length > 0 
    ? product.media.map(m => m.media_link) 
    : product?.images 
      ? Array.isArray(product.images) 
        ? product.images 
        : [product.images] 
      : [];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-4 border border-border w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground">
            {labels?.title?.[language]}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth"
            aria-label="Close"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* الصور */}
          <div>
            <label className="text-sm font-medium text-text-secondary mb-2 block">
              {labels?.images?.[language]}
            </label>
            {productImages?.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {productImages.map((img, index) => (
                  <div key={index} className="relative aspect-square rounded-lg overflow-hidden border border-border">
                    <Image
                      src={img}
                      alt={`${product?.name} - Image ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-40 bg-muted/50 rounded-lg border border-border">
                <div className="text-center">
                  <Icon name="ImageOff" size={48} className="mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-text-secondary">{labels?.noImages?.[language]}</p>
                </div>
              </div>
            )}
          </div>

          {/* المعلومات الأساسية */}
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-text-secondary mb-1 block">{labels?.name?.[language]}</label>
              <p className="text-lg font-semibold text-foreground">{product?.name}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-text-secondary mb-1 block">{labels?.price?.[language]}</label>
                <p className="text-lg font-bold text-primary">${product?.price}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-text-secondary mb-1 block">{labels?.discountedPrice?.[language]}</label>
                <p className="text-lg font-bold text-primary">${product?.discounted_price}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-text-secondary mb-1 block">{labels?.stock?.[language]}</label>
                <p className="text-base text-foreground">{product?.stock_count}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-text-secondary mb-1 block">{labels?.area?.[language]}</label>
                <p className="text-base text-foreground">{product?.area_name}</p>
              </div>
              {product?.contact_info && (
                <div>
                  <label className="text-sm font-medium text-text-secondary mb-1 block">{labels?.contact?.[language]}</label>
                  <p className="text-base text-foreground">{product?.contact_info}</p>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-text-secondary mb-1 block">{labels?.active?.[language]}</label>
                <p className="text-base text-foreground">{product?.is_active ? '✅' : '❌'}</p>
              </div>
              {product?.tags?.length > 0 && (
                <div className="col-span-full">
                  <label className="text-sm font-medium text-text-secondary mb-1 block">{labels?.tags?.[language]}</label>
                  <div className="flex flex-wrap gap-2">
                    {product.tags.map(tag => (
                      <span key={tag.id} className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-primary/10 text-primary">
                        {tag.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-text-secondary mb-1 block">{labels?.description?.[language]}</label>
              <p className="text-base text-foreground leading-relaxed">{product?.description}</p>
            </div>
          </div>

          {/* الأزرار */}
          <div className="flex gap-3 pt-4 border-t border-border">
            <Button variant="outline" fullWidth onClick={onClose}>{labels?.close?.[language]}</Button>
            <Button variant="default" fullWidth iconName="Edit" onClick={onEdit}>{labels?.edit?.[language]}</Button>
            <Button variant="destructive" fullWidth iconName="Trash2" onClick={onDelete}>{labels?.delete?.[language]}</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductViewModal;
